import { Controller } from "@hotwired/stimulus"

// Community reassurance on the result card for users who picked their skin
// type outright: an animated member count and what the type changes.
export default class extends Controller {
    static targets = ["memberCount", "payoff1Title", "payoff1Body", "payoff2Title", "payoff2Body"]
    static values = {
        knownSkinType: { type: String, default: '' },
        memberCounts: { type: Object, default: {} }
    }

    connect() {
        const type = this.knownSkinTypeValue
        const count = this.memberCountsValue[type] || 0
        // Round down so the displayed milestone never overstates membership.
        this.animateCount(count >= 1000 ? Math.floor(count / 1000) * 1000 : count, count >= 1000 ? "+" : "")
        this.renderPayoff(this.communityPayoffs[type] || this.communityPayoffs.default, type)
    }

    renderPayoff(payoff, type) {
        this.payoff1TitleTarget.textContent = payoff[0].title
        this.payoff1BodyTarget.textContent = payoff[0].body
        this.payoff2TitleTarget.textContent = `Match scores tuned for ${type} skin`
        this.payoff2BodyTarget.textContent = payoff[1].body
    }

    animateCount(target, suffix) {
        const element = this.memberCountTarget
        const format = value => value.toLocaleString("en-US")
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

        if (reduceMotion || target < 1000) {
            element.textContent = format(target) + suffix
            return
        }

        const duration = 700
        let start = null
        const step = timestamp => {
            if (start === null) start = timestamp
            const progress = Math.min(1, (timestamp - start) / duration)
            const eased = 1 - Math.pow(1 - progress, 3)
            const value = Math.round(target * eased / 1000) * 1000
            element.textContent = format(value) + (progress === 1 ? suffix : "")
            if (progress < 1) requestAnimationFrame(step)
        }
        element.textContent = format(0)
        requestAnimationFrame(step)
        // Frames stall in background WebViews; never leave the count at zero.
        setTimeout(() => { element.textContent = format(target) + suffix }, duration + 100)
    }

    get communityPayoffs() {
        return {
            'sensitive': [
                { title: "Irritants flagged on every product", body: "Fragrance, essential oils and common allergens are called out before you buy." },
                { body: "Gentle, low-risk formulas rise to the top of your results from here on." }
            ],
            'oily': [
                { title: "Pore-cloggers flagged on every product", body: "Heavy oils and comedogenic ingredients are called out before you buy." },
                { body: "Lightweight, oil-controlling formulas rise to the top of your results from here on." }
            ],
            'dry': [
                { title: "Drying ingredients flagged on every product", body: "Stripping cleansers and drying alcohols are called out before you buy." },
                { body: "Hydrating, barrier-supporting formulas rise to the top of your results from here on." }
            ],
            'combination': [
                { title: "Balance checked on every product", body: "Ingredients that over-dry your cheeks or add shine to your T-zone are called out before you buy." },
                { body: "Formulas that work for both zones rise to the top of your results from here on." }
            ],
            'normal': [
                { title: "Every product checked against your profile", body: "Irritants and pore-cloggers are called out before you buy." },
                { body: "Well-rounded formulas that keep your skin balanced rise to the top of your results from here on." }
            ],
            'default': [
                { title: "Every product checked against your profile", body: "Ingredients your skin type tends to dislike are called out before you buy." },
                { body: "Formulas suited to your skin type rise to the top of your results from here on." }
            ]
        }
    }
}
